/**
 * Forced onto every sanitized `<iframe>`. Card content can embed any host, so
 * nothing may be added here that lets a frame act on the page around it — most
 * of all `allow-top-navigation`, which would let an embed redirect the tab.
 *
 * `allow-same-origin` cannot be dropped either: without it a frame gets an
 * opaque origin and loses its own cookies, which signed-in embeds need.
 *
 * Pinned to the identical literal as the monolith's `HtmlCleaner::IFRAME_SANDBOX`
 * (`app/services/html_cleaner.rb`), which renders the same card content on the v1
 * app. kluein/klue#18793 added the last three in review because withholding them
 * silently breaks embeds rather than hardening them:
 *
 * - `allow-downloads` — without it Data Studio export and Drive/Docs "Download"
 *   are no-ops.
 * - `allow-modals` — print from a Docs/Slides preview.
 * - `allow-popups-to-escape-sandbox` — `allow-popups` alone hands the popup a
 *   COPY of these flags, so "Watch on YouTube" opens a tab that can neither
 *   submit a form nor navigate itself. Letting it out costs nothing we hold: it
 *   is a separate top-level context on the provider's own origin, its opener is
 *   the frame rather than our top window, and a document neither an ancestor of
 *   nor nested in our top browsing context is not "allowed to navigate" it.
 *
 * `allow-forms` and `allow-top-navigation` stay withheld, matching the monolith.
 */
export const IFRAME_SANDBOX =
  'allow-downloads allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts';

const EMBEDDABLE_PROTOCOLS = ['http:', 'https:'];

/**
 * Providers granted `allow-forms` on top of IFRAME_SANDBOX, mirroring
 * `HtmlCleaner::IFRAME_FORM_HOSTS` in the monolith. Office Online boots its
 * spreadsheet and document viewers by submitting a form to `xlembed.aspx`, so
 * on the default policy the browser blocks the submission and the embed renders
 * as an empty box — reported from production, a SharePoint workbook blank on
 * the card while intact in the editor.
 *
 * Suffix-matched rather than exact, because neither host set is enumerable:
 * SharePoint is per-tenant (`contoso.sharepoint.com`) and Office Online
 * per-region (`usc-excel.`, `euc-excel.`). `evil-sharepoint.com` and
 * `sharepoint.com.evil.test` do not match.
 *
 * Keep this minimal. The residual it accepts is that anyone who can stand up a
 * Microsoft-hosted tenant and get an iframe into card content gets `allow-forms`
 * on that frame — bounded by `allow-scripts` already being granted, so a hostile
 * frame can POST anywhere with `fetch` regardless.
 */
export const IFRAME_FORM_HOSTS = [
  'officeapps.live.com',
  'sharepoint.com',
  'onedrive.live.com',
];

export const IFRAME_SANDBOX_WITH_FORMS = `${IFRAME_SANDBOX} allow-forms`;

/**
 * The raw attribute goes to `URL()` untouched, because `URL()` already performs
 * exactly the whitespace handling the browser's own parser does: leading and
 * trailing C0-and-space stripped, tab/CR/LF removed from anywhere.
 *
 * Pre-trimming is not equivalent and is not safe. JS `trim()` also strips
 * Unicode whitespace the URL parser keeps, so `\u00a0https://sharepoint.com/x`
 * would be read here as SharePoint while the browser, unable to parse it as
 * absolute, resolves it as a *relative* URL on the Klue origin — a same-origin
 * frame handed `allow-forms`.
 */
export function iframeAllowsForms(src: string | undefined): boolean {
  try {
    const { protocol, hostname } = new URL(String(src ?? ''));

    if (!EMBEDDABLE_PROTOCOLS.includes(protocol)) return false;

    const host = hostname.toLowerCase();

    return IFRAME_FORM_HOSTS.some(
      (domain) => host === domain || host.endsWith(`.${domain}`),
    );
  } catch {
    return false;
  }
}

/**
 * Forced onto every sanitized `<iframe>` alongside the sandbox. An
 * author-supplied `unsafe-url` would hand the embedded host the full Klue URL,
 * which carries card, profile and search identifiers.
 */
export const IFRAME_REFERRER_POLICY = 'strict-origin-when-cross-origin';

/**
 * Permissions-Policy features an embed may keep. Sandbox and `allow` are
 * independent: without this filter, content could hand an arbitrary frame
 * camera, microphone or geolocation access through `allow` alone.
 *
 * Scoped to what the supported embeds ask for: YouTube's published embed
 * string minus `web-share`, plus `fullscreen` for Gong and Slides.
 */
export const IFRAME_ALLOW_FEATURES = [
  'accelerometer',
  'autoplay',
  'clipboard-write',
  'encrypted-media',
  'fullscreen',
  'gyroscope',
  'picture-in-picture',
];

/**
 * Stripped from every iframe regardless of what the caller allows. `srcdoc`
 * inherits the embedder's origin, so under the forced `allow-same-origin` its
 * script would run as us, with access to `top`. No supported embed needs it.
 */
export const IFRAME_FORBIDDEN_ATTRS = ['srcdoc'];

/** Drops any Permissions-Policy directive not in IFRAME_ALLOW_FEATURES. */
export function filterIframeAllow(allow: string): string {
  return allow
    .split(';')
    .map((directive) => directive.trim())
    .filter((directive) =>
      IFRAME_ALLOW_FEATURES.includes(directive.split(/\s+/)[0].toLowerCase()),
    )
    .join('; ');
}

/** True for `http(s):` URLs only — `javascript:`, `data:` and `blob:` are not embeddable. */
function isEmbeddableUrl(url: string): boolean {
  try {
    return EMBEDDABLE_PROTOCOLS.includes(new URL(url).protocol);
  } catch {
    return false;
  }
}

/**
 * Applies the sanitizer's iframe hardening, in place, to a parsed attribute
 * bag — for `transformHTML`, which has attributes rather than an HTML string.
 * Match its *effects*, not its structure: sanitizer.ts splits the work across
 * DOMPurify lifecycle stages for reasons that don't apply here.
 */
export function hardenIframeAttribs(attribs: Record<string, string>): void {
  attribs.sandbox = iframeAllowsForms(attribs.src)
    ? IFRAME_SANDBOX_WITH_FORMS
    : IFRAME_SANDBOX;
  attribs.referrerpolicy = IFRAME_REFERRER_POLICY;
  IFRAME_FORBIDDEN_ATTRS.forEach((attr) => delete attribs[attr]);

  if (attribs.src && !isEmbeddableUrl(attribs.src)) delete attribs.src;
  if (attribs.allow) attribs.allow = filterIframeAllow(attribs.allow);
}
